import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import type {
  ProjectPage,
  ProjectPageFormData,
  ProjectPageQueryParams,
  ProjectPageListResponse,
  ProjectPageWithGallery
} from '@/types/projectPages'
import * as api from '../api/projectPagesApi'

/**
 * Project Pages React Query Hooks
 *
 * Provides a clean API for managing project page data with:
 * - Automatic caching and revalidation
 * - Optimistic updates
 * - Error handling and toast notifications
 * - Type-safe mutations
 */

// Query keys for cache management
export const projectPagesKeys = {
  all: ['project-pages'] as const,
  lists: () => [...projectPagesKeys.all, 'list'] as const,
  list: (params: ProjectPageQueryParams) => [...projectPagesKeys.lists(), params] as const,
  details: () => [...projectPagesKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectPagesKeys.details(), id] as const,
  bySlug: (slug: string) => [...projectPagesKeys.all, 'slug', slug] as const,
  byGallery: (galleryId: number) => [...projectPagesKeys.all, 'gallery', galleryId] as const
}

/**
 * Fetch project pages with filtering and pagination
 */
export function useProjectPages(
  params: ProjectPageQueryParams,
  options?: Omit<UseQueryOptions<ProjectPageListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: projectPagesKeys.list(params),
    queryFn: () => api.fetchProjectPages(params),
    staleTime: 30000, // 30 seconds
    ...options
  })
}

/**
 * Fetch a single project page by ID
 */
export function useProjectPage(
  id: string,
  options?: Omit<UseQueryOptions<ProjectPageWithGallery>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: projectPagesKeys.detail(id),
    queryFn: () => api.fetchProjectPageById(id),
    enabled: !!id,
    ...options
  })
}

/**
 * Fetch a project page by slug (for public viewing)
 */
export function useProjectPageBySlug(
  slug: string,
  options?: Omit<UseQueryOptions<ProjectPageWithGallery>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: projectPagesKeys.bySlug(slug),
    queryFn: () => api.fetchProjectPageBySlug(slug),
    enabled: !!slug,
    ...options
  })
}

/**
 * Fetch a project page by gallery ID
 * Returns null if no project page exists for this gallery
 *
 * @see GalleryJobPage.tsx - Used to conditionally show "View Project Page" vs "Create Project Page"
 */
export function useProjectPageByGallery(
  galleryId: number | null | undefined,
  options?: Omit<UseQueryOptions<ProjectPageWithGallery | null>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: projectPagesKeys.byGallery(galleryId ?? 0),
    queryFn: () => api.fetchProjectPageByGalleryId(galleryId!),
    enabled: !!galleryId && galleryId > 0,
    staleTime: 30000, // 30 seconds - matches other project page queries
    ...options
  })
}

/**
 * Create a new project page
 */
export function useCreateProjectPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: ProjectPageFormData) => api.createProjectPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      toast({
        title: 'Success',
        description: 'Project page created successfully'
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create project page',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Update an existing project page
 */
export function useUpdateProjectPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ProjectPageFormData> }) =>
      api.updateProjectPage(id, updates),
    onSuccess: (data) => {
      // Invalidate list and detail queries
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.detail(data.id) })
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.bySlug(data.slug) })

      toast({
        title: 'Success',
        description: 'Project page updated successfully'
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update project page',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Delete a project page
 */
export function useDeleteProjectPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => api.deleteProjectPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      toast({
        title: 'Success',
        description: 'Project page deleted successfully'
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete project page',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Check slug uniqueness
 */
export function useCheckSlugUnique() {
  return useMutation({
    mutationFn: ({ slug, excludeId }: { slug: string; excludeId?: string }) =>
      api.checkSlugUnique(slug, excludeId)
  })
}

/**
 * Publish a project page
 */
export function usePublishProjectPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, publishAt }: { id: string; publishAt?: string }) =>
      api.publishProjectPage(id, publishAt),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.detail(data.id) })

      const isScheduled = data.publish_at && new Date(data.publish_at) > new Date()
      toast({
        title: 'Success',
        description: isScheduled
          ? `Project page scheduled for ${new Date(data.publish_at!).toLocaleString()}`
          : 'Project page published successfully'
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to publish project page',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Unpublish a project page
 */
export function useUnpublishProjectPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => api.unpublishProjectPage(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.detail(data.id) })

      toast({
        title: 'Success',
        description: 'Project page unpublished successfully'
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unpublish project page',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Archive a project page
 */
export function useArchiveProjectPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => api.archiveProjectPage(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.detail(data.id) })

      toast({
        title: 'Success',
        description: 'Project page archived successfully'
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to archive project page',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Toggle featured status
 */
export function useToggleFeatured() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, featured }: { id: string; featured: boolean }) =>
      api.toggleFeatured(id, featured),
    onMutate: async ({ id, featured }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: projectPagesKeys.detail(id) })
      const previous = queryClient.getQueryData<ProjectPage>(projectPagesKeys.detail(id))

      if (previous) {
        queryClient.setQueryData<ProjectPage>(projectPagesKeys.detail(id), {
          ...previous,
          featured
        })
      }

      return { previous }
    },
    onError: (error: Error, _variables, context) => {
      // Rollback optimistic update
      if (context?.previous) {
        queryClient.setQueryData(projectPagesKeys.detail(context.previous.id), context.previous)
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to toggle featured status',
        variant: 'destructive'
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })
      toast({
        title: 'Success',
        description: data.featured ? 'Marked as featured' : 'Removed from featured'
      })
    }
  })
}

/**
 * Bulk update project pages
 */
export function useBulkUpdateProjectPages() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Partial<ProjectPage> }) =>
      api.bulkUpdateProjectPages(ids, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })

      toast({
        title: 'Success',
        description: `Updated ${variables.ids.length} project page(s)`
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update project pages',
        variant: 'destructive'
      })
    }
  })
}

/**
 * Bulk delete project pages
 */
export function useBulkDeleteProjectPages() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteProjectPages(ids),
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: projectPagesKeys.lists() })

      toast({
        title: 'Success',
        description: `Deleted ${ids.length} project page(s)`
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete project pages',
        variant: 'destructive'
      })
    }
  })
}
